import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import MarketplaceShell from './MarketplaceShell';
import { MktCard, MktButton, BackLink, FreeBadge } from './marketplaceUi';
import {
  getStudy, submitStudy, dollars,
  type Study, type StudySelection,
} from './marketplaceApi';

/**
 * Simulation review — the last look before a study runs. Used by BOTH the free
 * and paid flows: free studies get a "Run study" CTA, paid studies get
 * "Review & pay" with the cost and Stripe checkout. Surfaces the resolved Twin
 * segments (names highlighted) and a summary of the inputs the user entered.
 */
const ReviewScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const stateId = (location.state as { studyId?: number } | null)?.studyId;
  const studyId = stateId ?? (params.get('study') ? Number(params.get('study')) : undefined);

  const [study, setStudy] = useState<Study | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!studyId) { navigate('/marketplace/run', { replace: true }); return; }
    getStudy(studyId).then(setStudy).catch((e) => setError(e.message));
  }, [studyId, navigate]);

  const onRun = async () => {
    if (!studyId) return;
    setBusy(true);
    setError(null);
    try {
      const res = await submitStudy(studyId);
      if (res.checkout_url) { window.location.href = res.checkout_url; return; }
      navigate('/marketplace/run/processing', { state: { studyId } });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not start your study');
    } finally {
      setBusy(false);
    }
  };

  if (error) return <MarketplaceShell maxWidth="max-w-2xl"><div className="text-red-600">{error}</div></MarketplaceShell>;
  if (!study) return <MarketplaceShell maxWidth="max-w-2xl"><div className="text-[13px] text-[#6b6b6b]">Loading…</div></MarketplaceShell>;

  const selections = study.selections || [];
  const isFree = study.is_free;

  return (
    <MarketplaceShell maxWidth="max-w-2xl">
      <BackLink label="Set up study" onClick={() => navigate(-1)} className="mb-3" />
      <h1 className="text-[22px] font-medium text-[#111]">{isFree ? 'Review your study' : 'Review & pay'}</h1>
      <p className="mt-1.5 text-[13px] text-[#6b6b6b]">One last look before your study runs.</p>

      <MktCard className="mt-5 divide-y divide-black/10 p-0">
        {/* Use case */}
        <div className="flex items-center justify-between p-5">
          <span className="text-[11px] uppercase tracking-[0.04em] text-[#999]">Use case</span>
          <span className="text-[13px] font-medium capitalize text-[#111]">{study.use_case}</span>
        </div>

        {/* Audience — Twin names highlighted */}
        <div className="p-5">
          <div className="mb-2.5 text-[11px] uppercase tracking-[0.04em] text-[#999]">Audience</div>
          <ul className="flex flex-col gap-2">
            {selections.map((s, i) => (
              <li key={i} className="flex items-center justify-between gap-3">
                <TwinName name={s.name || prettySlug(s)} />
                <span className="flex-shrink-0 text-[12px] text-[#6b6b6b]">{s.twin_count ?? '—'} Twins</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Inputs the user entered */}
        <InputsSummary study={study} />

        {/* Total + cost */}
        <div className="flex items-center justify-between p-5">
          <div>
            <div className="text-[11px] uppercase tracking-[0.04em] text-[#999]">Total Twins</div>
            <div className="text-[15px] font-medium text-[#111]">{study.twin_count}</div>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-[0.04em] text-[#999]">{isFree ? 'Cost' : 'Total cost'}</div>
            {isFree
              ? <div className="mt-0.5"><FreeBadge>Free</FreeBadge></div>
              : <div className="text-[20px] font-medium text-[#111]">{dollars(study.amount_cents)}</div>}
          </div>
        </div>
      </MktCard>

      <div className="mt-6">
        <MktButton disabled={busy} onClick={onRun}>
          {busy
            ? (isFree ? 'Starting your study…' : 'Starting checkout…')
            : (isFree ? 'Run study →' : `Pay ${dollars(study.amount_cents)} and run →`)}
        </MktButton>
        {error && <div className="mt-3 text-[13px] text-red-600">{error}</div>}
      </div>
    </MarketplaceShell>
  );
};

/** A Twin segment name, visually highlighted (teal chip) so it stands out. */
const TwinName: React.FC<{ name: string }> = ({ name }) => (
  <span className="inline-flex items-center rounded-md bg-[#E8FCFA] px-2 py-1 text-[13px] font-medium text-[#0B7C74]">
    {name}
  </span>
);

const prettySlug = (s: StudySelection): string => {
  const raw = s.archetype_id || s.sub_category || 'Selected Twins';
  return raw.replace(/__\d+$/, '').replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

/** Per-use-case summary of the inputs the user filled in. Defensive: only
 *  renders sections that are present. */
const InputsSummary: React.FC<{ study: Study }> = ({ study }) => {
  const inputs = (study.inputs || {}) as Record<string, any>;
  const goal = (inputs.goal || inputs.goal_of_simulation || '') as string;
  const context = (inputs.context || '') as string;
  const questions: any[] = Array.isArray(inputs.questions) ? inputs.questions : [];

  const questionText = (q: any): string =>
    (typeof q === 'string' ? q : (q?.question_text || q?.text || q?.question || q?.title || '')) || '';

  const hasContent = goal || context || questions.length;
  if (!hasContent) {
    return (
      <div className="p-5">
        <div className="mb-1 text-[11px] uppercase tracking-[0.04em] text-[#999]">Your inputs</div>
        <p className="text-[13px] text-[#6b6b6b]">Your {study.use_case} inputs are saved and ready to run.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-5">
      {goal && (
        <div>
          <div className="mb-1 text-[11px] uppercase tracking-[0.04em] text-[#999]">Goal</div>
          <p className="text-[13px] leading-relaxed text-[#374151]">{goal}</p>
        </div>
      )}
      {context && (
        <div>
          <div className="mb-1 text-[11px] uppercase tracking-[0.04em] text-[#999]">Context</div>
          <p className="text-[13px] leading-relaxed text-[#374151]">{context}</p>
        </div>
      )}
      {questions.length > 0 && (
        <div>
          <div className="mb-1.5 text-[11px] uppercase tracking-[0.04em] text-[#999]">
            Questions <span className="text-[#bbb]">({questions.length})</span>
          </div>
          <ol className="list-decimal space-y-1 pl-5 text-[13px] leading-relaxed text-[#374151] marker:text-[#999]">
            {questions.map((q, i) => <li key={i}>{questionText(q) || <span className="text-[#999]">Question {i + 1}</span>}</li>)}
          </ol>
        </div>
      )}
    </div>
  );
};

export default ReviewScreen;
